/* Runtime-cache state hook — the app's live view of Cache Storage.
 *
 * Tracks which versions are really cached, their real byte sizes, and the browser
 * storage estimate, refreshing after every cache mutation so the version picker
 * and Settings manager always reflect what's actually on disk. On first mount it
 * also requests persistent storage to resist quota eviction of cached runtimes. */

import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  cachedVersions,
  cachedBytes as readCachedBytes,
  storageEstimate,
  requestPersistentStorage,
  removeCached,
  clearAllCached,
} from '../../runtime/runtime-cache';
import { ALL_VERSIONS } from '../../runtime/version-registry';

export interface RuntimeCacheState {
  downloaded: string[];
  cachedBytes: Record<string, number>;
  /** null if unavailable. */
  estimate: StorageEstimate | null;
  refresh: () => Promise<void>;
  removeOne: (v: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

export function useRuntimeCache(): RuntimeCacheState {
  const [downloaded, setDownloaded] = useState<string[]>([]);
  const [cachedBytes, setCachedBytes] = useState<Record<string, number>>({});
  const [estimate, setEstimate] = useState<StorageEstimate | null>(null);

  const refresh = useCallback(async () => {
    const cv = await cachedVersions(ALL_VERSIONS);
    setDownloaded(cv);
    const entries = await Promise.all(cv.map(async (v) => [v, await readCachedBytes(v)] as const));
    setCachedBytes(Object.fromEntries(entries));
    setEstimate(await storageEstimate());
  }, []);

  useEffect(() => {
    void requestPersistentStorage();
    void refresh();
  }, [refresh]);

  const removeOne = useCallback(
    async (v: string) => {
      await removeCached(v);
      await refresh();
    },
    [refresh],
  );

  const clearAll = useCallback(async () => {
    await clearAllCached();
    await refresh();
  }, [refresh]);

  return { downloaded, cachedBytes, estimate, refresh, removeOne, clearAll };
}
